import * as React from 'react';
import { useState } from 'react';
import { dateFormat } from '../utils';
import { UserContext } from '../functions/Auth';

// component
import Lexical from "./Lexical";

// mui
import { useTheme } from "@mui/material";
import Avatar from '@mui/material/Avatar';
import Card from '@mui/material/Card';
//import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';

// icons
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FaceIcon from '@mui/icons-material/Face';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ShortcutIcon from '@mui/icons-material/Shortcut';
//import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';

// types
type CommentCardProps = {
    body: string,
    commentId: number,
    setIsEdit: React.Dispatch<React.SetStateAction<boolean>>,
    setIsOpenConfirmDialog: React.Dispatch<React.SetStateAction<boolean>>,
    setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>,
    setIsReply: React.Dispatch<React.SetStateAction<boolean>>,
    setTargetEditComment: React.Dispatch<React.SetStateAction<number>>,
    setTargetEditCommentEditorState: React.Dispatch<React.SetStateAction<string | undefined>>,
    setTargetDeleteComment: React.Dispatch<React.SetStateAction<number>>,
    setTargetReplyCommentNodeAsJSON: React.Dispatch<React.SetStateAction<string>>,
    setTargetReplyUser: React.Dispatch<React.SetStateAction<{userId: number, userName: string}>>,
    postTime: string,
    userName: string,
    userId: number,
}

export default function CommentCard(props: CommentCardProps) {
    const {
        body,
        commentId,
        postTime,
        setIsEdit,
        setIsOpenConfirmDialog,
        setIsOpenModal,
        setIsReply,
        setTargetEditComment,
        setTargetEditCommentEditorState,
        setTargetDeleteComment,
        setTargetReplyCommentNodeAsJSON,
        setTargetReplyUser,
        userName,
        userId,
    } = props;
    const theme = useTheme();
    const userInfo = React.useContext(UserContext);
    const loginUserId = userInfo?.userId as number;

    // lexical
    const [currentContentAsJSON, setCurrentContentAsJSON] = useState<string>("");

    // action menu
    const actionMenuId = 'comment-card-action-menu';
    const [actionMenuAnchorEl, setActionMenuAnchorEl] = React.useState<null | HTMLElement>(null);
    const isActionMenuOpen = Boolean(actionMenuAnchorEl);
    const handleActionMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setActionMenuAnchorEl(event.currentTarget);
    };
    const handleActionMenuClose = () => {
        setActionMenuAnchorEl(null);
    };

    return (
        <Card
            sx={{
                px: 0,
                pt: 3,
                pb: 0.5,
                borderTopWidth: 1,
                borderTopStyle: 'solid',
                borderTopColor: theme.palette.grey[600],
                borderRadius: 'unset',
                boxShadow: 'unset',
                flexGrow: 1,
                overflow: 'visible',
            }}
        >
            <CardHeader
                avatar={
                    <Avatar variant="rounded" aria-label="author">
                        <FaceIcon sx={{ color: (theme) => theme.palette.grey[600], fontSize: 40 }} />
                    </Avatar>
                }
                action={
                    <>
                        {userId === loginUserId
                            ? // ログインユーザーの投稿の場合
                            <>
                                <Tooltip title="編集">
                                    <IconButton
                                        onClick={() => {
                                            setTargetEditComment(commentId);
                                            setTargetEditCommentEditorState(currentContentAsJSON);
                                            setIsEdit(true);
                                            setIsOpenModal(true);
                                            handleActionMenuClose();
                                        }}
                                        aria-label="edit"
                                    >
                                        <EditOutlinedIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="オプション">
                                    <IconButton
                                        onClick={handleActionMenuOpen}
                                        aria-label="options"
                                        aria-controls="comment-card-action-menu"
                                        aria-haspopup="true"
                                    >
                                        <MoreHorizIcon />
                                    </IconButton>
                                </Tooltip>
                                <Menu
                                    anchorEl={actionMenuAnchorEl}
                                    anchorOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    id={actionMenuId}
                                    keepMounted
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    open={isActionMenuOpen}
                                    onClose={handleActionMenuClose}
                                    sx={{
                                        [`& .MuiSvgIcon-root`]: {
                                            mr: 0.5,
                                            fontSize: 20,
                                        }
                                    }}
                                >
                                    <MenuItem onClick={() => {
                                        setTargetReplyCommentNodeAsJSON(body);
                                        setTargetReplyUser({ userId, userName });
                                        setIsReply(true);
                                        setIsOpenModal(true);
                                        handleActionMenuClose();
                                    }}>
                                        <ShortcutIcon sx={{ transform: 'scale(-1, 1)'}} />返信
                                    </MenuItem>
                                    <MenuItem onClick={() => {
                                        setTargetDeleteComment(commentId);
                                        setIsOpenConfirmDialog(true);
                                        handleActionMenuClose();
                                    }}>
                                        <DeleteOutlinedIcon />削除
                                    </MenuItem>
                                </Menu>
                            </>
                            : // ログインユーザー以外の投稿の場合
                            <Tooltip title="返信">
                                <IconButton
                                    onClick={() => {
                                        setTargetReplyCommentNodeAsJSON(body);
                                        setTargetReplyUser({ userId, userName });
                                        setIsReply(true);
                                        setIsOpenModal(true);
                                        handleActionMenuClose();
                                    }}
                                    aria-label="reply"
                                >
                                    <ShortcutIcon sx={{ transform: 'scale(-1, 1)' }} />
                                </IconButton>
                            </Tooltip>
                        }
                    </>
                }
                title={userName}
                subheader={dateFormat(postTime)}
                sx={{
                    px: 0,
                    py: 0,
                    '& .MuiCardHeader-avatar': {
                        mr: 1,
                        '& .MuiAvatar-root': {
                            backgroundColor: "transparent",
                        }
                    },
                    [`& .MuiCardHeader-action`]: {
                        position: "relative",
                        alignSelf: "unset",
                        my: 0,
                        mr: 0,
                    },
                }}
            />
            <CardContent sx={{ px: 0, pt: 1, pb: 0 }}>
                <Lexical
                    defaultContentAsJSON={body}
                    isEditable={false}
                    setCurrentContentAsJSON={setCurrentContentAsJSON}
                />
            </CardContent>
            {/*<CardActions*/}
            {/*    sx={{*/}
            {/*        flexDirection: "row-reverse",*/}
            {/*        px: 0,*/}
            {/*        pt: 1,*/}
            {/*        pb: 0,*/}
            {/*    }}>*/}
            {/*    <IconButton*/}
            {/*        color="primary"*/}
            {/*        sx={{*/}
            {/*            '&>span': {*/}
            {/*                ml: 0.5,*/}
            {/*                fontSize: 14,*/}
            {/*                color: (theme) => theme.palette.grey[600],*/}
            {/*            }*/}
            {/*        }}*/}
            {/*        aria-label="thumbup"*/}
            {/*    >*/}
            {/*        <ThumbUpAltOutlinedIcon />*/}
            {/*        <span>25</span>*/}
            {/*    </IconButton>*/}
            {/*</CardActions>*/}
        </Card>
    );
}